import type { AblyChannel } from '@/ably'

const SITE_ID = import.meta.env.GLP_SITE_ID
const siteChannel = (name: string) => (SITE_ID ? `${name}.${SITE_ID}` : name)

type ChannelKeys = keyof typeof RealTimeEvents.Channels
export type Channels = (typeof RealTimeEvents.Channels)[ChannelKeys]
export type ChannelName = Channels | `${Channels}.${string}`

type EventKeys = keyof typeof RealTimeEvents.Events
export type Events = (typeof RealTimeEvents.Events)[EventKeys]

export type ContentUpdateEvent<T> = {
  action: 'updated' | 'deleted'
  code: string
  data?: T
  published_at: string
}

/**
 * Real-time events service.
 *
 * @see https://laravel.com/docs/12.x/broadcasting
 * @see https://ably.com/docs/
 * @see https://github.com/ably-forks/laravel-echo
 */
export class RealTimeEvents {
  private static channelsMap: Map<ChannelName, AblyChannel> = new Map()

  /**
   * The list of available channels.
   */
  public static Channels = Object.freeze({
    InstantWin: siteChannel('instant-win'),
    InstantWinResults: siteChannel('instant-win-results'),
    Orders: 'orders',
    Leaderboard: siteChannel('leaderboard'),
    Customer: 'customer',
    ContentUpdates: siteChannel('content-updates'),
  })

  /**
   * The list of available events to subscribe to.
   */
  public static Events = Object.freeze({
    InstantWinCountersUpdate: '.instant-win.counters.update',
    InstantWinResultsUpdate: '.instant-win.results.update',
    OrderStatusUpdate: '.order.status.update',
    LeaderboardCounterUpdate: '.leaderboard.counter.update',
    LeaderboardStandingsUpdate: '.leaderboard.standings.update',
    LeaderboardUserStandingUpdate: '.leaderboard.user-standing.update',
    RewardsClaimed: '.rewards.claimed',
    InstantWinPublished: '.instant-win.published',
    PrizePublished: '.prize.published',
    CampaignPublished: '.campaign.published',
  })

  /**
   * Creates a properly typed channel name for a specific resource
   *
   * @param {ChannelKeys} resourceType The type of resource from predefined channels
   * @param {string | number} resourceId The unique identifier for the resource
   * @returns {ChannelName} A properly typed channel name
   */
  public static getChannel(
    resourceType: ChannelKeys,
    resourceId: string | number
  ): ChannelName {
    // Get the actual channel value from the enum
    const channelPrefix = RealTimeEvents.Channels[resourceType]
    return `${channelPrefix}.${resourceId}`
  }

  /**
   * Enters a channel and returns the channel instance.
   *
   * The channel event will still need to be subscribed to.
   *
   * @param {ChannelName} channelName
   * @returns  {AblyChannel}
   */
  public static enterChannel(
    channelName: ChannelName,
    publicChannel: boolean
  ): AblyChannel {
    if (this.channelsMap.has(channelName)) {
      return this.channelsMap.get(channelName) as AblyChannel
    }

    let channel: AblyChannel

    if (publicChannel) {
      channel = window.Echo.channel(channelName)
    } else {
      channel = window.Echo.private(channelName)
    }

    this.channelsMap.set(channelName, channel)

    return channel
  }

  /**
   * Leaves a channel.
   *
   * @param {string} channelName Channel name.
   * @param {boolean} publicChannel If true, the channel will be a public channel - prefixed with 'public:'. Otherwise, it will be a private channel - prefixed with 'private:'.
   * @returns {boolean} True if the channel was successfully left. False if the channel does not exist.
   */
  public static leaveChannel(
    channelName: ChannelName,
    publicChannel: boolean = true
  ): boolean {
    const channel = this.channelsMap.get(channelName)

    if (!channel) {
      return false
    }

    window.Echo.leaveChannel(
      `${publicChannel ? 'public' : 'private'}:${channelName}`
    )
    return this.channelsMap.delete(channelName)
  }

  /**
   * Subscribes to a channel event.
   *
   * If the channel does not exist, it will be created.
   *
   * @param {ChannelName} channelName Channel name.
   * @param {string} event Event name.
   * @param {Function} callback A function that will be called when the event is triggered.
   */
  public static subscribe(
    channelName: ChannelName,
    event: Events,
    publicChannel: boolean = true,
    callback: (eventResponse: any) => void
  ): void {
    const channel = RealTimeEvents.enterChannel(channelName, publicChannel)

    channel.listen(event, callback)
  }

  /**
   * Unsubscribes from a channel event.
   *
   * @param {ChannelName} channelName Channel name.
   * @param {string} event Event name.
   */
  public static unsubscribe(channelName: ChannelName, event: Events): void {
    const channel = this.channelsMap.get(channelName)

    if (!channel) {
      return
    }

    channel.stopListening(event)
  }
}
